import RadioGroup from '@components/v2/form/RadioGroup/RadioGroup';
import Modal from '@components/v2/ui/Modal/Modal';
import { Stack } from '@mui/material';
import { useEffect } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useCatalogModalStore } from '../../../../stores/CatalogModal/CatalogModalStore';
import useIsAdminSeller from '../../../../stores/ShowUnavailableProducts/hooks/useIsAdminSeller';
import { useTranslation } from '../../../translation/TranslationContext';
import ClassificationTypeSelect from './components/ClassificationTypeSelect/ClassificationTypeSelect';
import ExportImageModelSelect from './components/ExportImageModelSelect/ExportImageModelSelect';
import PriceTableAutoComplete from './components/PriceTableAutocomplete/PriceTableAutocomplete';
import useCatalogExportModal from './hooks/useCatalogExportModal';
import { ExportMethod, ExportQuality } from './types/catalogExportModal.types';
import { ExportOptions, getQualityOptions, getTypeOptions } from './utils/catalogExportModal.utils';

const CatalogExportModal = () => {
  const { translate } = useTranslation();
  const isAdminSeller = useIsAdminSeller();
  const {
    reset,
    isModalOpen,
    closeModal,
    onSubmit,
    isLoading,
    catalog,
    initialClassificationTypeId,
  } = useCatalogModalStore();
  const {
    values,
    getHelperText,
    isExportTypeXlsSelected,
    errorHelperTextPriceTable,
    handleSubmit,
    handleValueTypeChange,
    handleChangeImageModel,
    handleValueQualityChange,
    handleValueExportViaChange,
    handleValuePriceTableIdChange,
    handleValueClassificationIdChange,
  } = useCatalogExportModal({
    onSubmit,
    translate,
    isLoading,
    isAdminSeller,
    isModalOpen,
    initialClassificationTypeId,
  });

  useEffect(
    () => () => {
      reset();
    },
    [],
  );

  const isXlsx = isExportTypeXlsSelected();

  return (
    <Modal
      isOpen={isModalOpen}
      size="expanded"
      header={{ title: translate(LangEnum.CATALOGS_EXPORT_MODAL_TITLE) }}
      onClose={closeModal}
      footer={{
        primaryAction: {
          label: LangEnum.CATALOGS_EXPORT_MODAL_CONFIRM_LABEL,
          isLoading,
          onClick: handleSubmit,
        },
        secondaryAction: {
          label: LangEnum.CATALOGS_EXPORT_MODAL_CANCEL_LABEL,
          onClick: closeModal,
        },
      }}
    >
      <Stack spacing={2.5} useFlexGap>
        <RadioGroup
          name="exportType"
          label={translate(LangEnum.CATALOGS_EXPORT_TYPE_LABEL)}
          value={values.exportType}
          options={getTypeOptions(translate)}
          onChange={handleValueTypeChange}
        />
        {!isXlsx && (
          <RadioGroup
            name="exportQuality"
            label={translate(LangEnum.CATALOGS_EXPORT_QUALITY_LABEL)}
            value={values.exportQuality}
            options={getQualityOptions(translate)}
            onChange={handleValueQualityChange}
          />
        )}
        <RadioGroup
          name="exportMethod"
          label={translate(LangEnum.CATALOGS_EXPORT_METHOD_LABEL)}
          value={values.exportMethod}
          helperText={getHelperText(values)}
          options={ExportOptions.map((item) => ({
            ...item,
            disabled:
              isXlsx ||
              (values.exportQuality === ExportQuality.HIGH && item.value === ExportMethod.DOWNLOAD),
          }))}
          onChange={handleValueExportViaChange}
        />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2.5, sm: 2 }}
          useFlexGap
          sx={{ marginTop: 0.5 }}
        >
          {!isXlsx && (
            <PriceTableAutoComplete
              handleChange={handleValuePriceTableIdChange}
              errorHelperText={errorHelperTextPriceTable}
              disabled={isXlsx}
              priceTableIdInitial={values.priceTableId}
            />
          )}
          <ExportImageModelSelect
            exportImageModel={values.exportImageModel}
            handleChangeImageModel={handleChangeImageModel}
          />
        </Stack>
        {!isXlsx && (
          <ClassificationTypeSelect
            catalogId={catalog?.id}
            disabled={isXlsx}
            handleChange={handleValueClassificationIdChange}
            initialClassificationTypeId={initialClassificationTypeId ?? undefined}
          />
        )}
      </Stack>
    </Modal>
  );
};

export default CatalogExportModal;
